import { PropsWithChildren } from "react";
import { useParams } from "react-router";

import { ClientFileEntry, ClientFileEntryKind, ClientFileInfoLineContext } from "@shared";

import { ChangeHistorySection } from "@/api";
import { ClientByIdParams } from "@/router/ClientRoutes";

import { ClientChangeInfo } from "./ClientChangeInfo";
import { ClientRouteChangeInfo } from "./ClientRouteChangeInfo";
import { useInvalidateChangeHistoryOnSave } from "./hooks/useInvalidateChangeHistoryOnSave";

// Which fields a history covers is a section. A page's follows from the route, so only the records
// a dialog opens need naming here — the claims page (Verbindlichkeiten) edits one claim (Forderung)
// at a time, and the client's own creditor (Gläubiger:in) is edited from within that one.
const ENTRY_SECTIONS: Record<ClientFileEntryKind, ChangeHistorySection> = {
  [ClientFileEntryKind.Claim]: ChangeHistorySection.Claims,
  [ClientFileEntryKind.ClientSpecificCreditor]: ChangeHistorySection.ClientSpecificCreditor,
};

const renderChangeInfo = (entry?: ClientFileEntry) =>
  entry ? (
    <ClientChangeInfo scope={{ section: ENTRY_SECTIONS[entry.kind], entryId: entry.id }} />
  ) : (
    <ClientRouteChangeInfo />
  );

/**
 * Puts the internal app's change-info line within reach of every saveable view of the client case
 * file (Kundenakt), including the views living in @shared, which a per-page or wrapper approach
 * could not reach. Wraps the client page's routes, so it outlives the individual lines: they come
 * and go with the page and its dialogs, while a save has to refresh this client's cached history
 * whichever one is showing.
 */
export function ClientChangeInfoProvider({ children }: PropsWithChildren) {
  const { id } = useParams<ClientByIdParams>();

  // Keyed on the route id, which is what both change-history queries key on — invalidation matches
  // their key by prefix, so the id it passes has to be the very same value.
  useInvalidateChangeHistoryOnSave(id);

  return <ClientFileInfoLineContext.Provider value={renderChangeInfo}>{children}</ClientFileInfoLineContext.Provider>;
}
